import { model as router } from '!/router'
import { model as cartModel } from '$/cart'
import { F, T } from '@/helpers'
import type { ICartProperties } from '@setplex/tria-api'
import { PurchasePaymentChannelEnum, api } from '@setplex/tria-api'
import { createEvent, createStore, sample } from 'effector'
import { model as genericPaymentModel } from '~/features/payment/generic'
import { PaymentStepsGravy } from '~/features/payment/interfaces/gravy'
import { $isPending } from './form'

/*
  Flow cases
   1️⃣ - SelectPriceSettings - generic flow is used
   2️⃣ - Gravy form
   3️⃣ - SuccessPurchaseConfirmation - generic flow is used
 */

export const openGravyPopup = createEvent()
export const closeGravyPopup = createEvent()

sample({
  clock: genericPaymentModel.openPaymentPopupWithType,
  filter: (channel) =>
    channel?.paymentChannel === PurchasePaymentChannelEnum.GR4VY_GATEWAY,
  target: openGravyPopup,
})

sample({
  clock: genericPaymentModel.closePaymentPopupWithType,
  filter: (channel) =>
    channel?.paymentChannel === PurchasePaymentChannelEnum.GR4VY_GATEWAY,
  target: closeGravyPopup,
})

export const $gravyPopupIsOpened = createStore<boolean>(false)
  .on(openGravyPopup, T)
  .on(closeGravyPopup, F)
  .on(router.$pathname, F)

sample({
  clock: $gravyPopupIsOpened,
  target: genericPaymentModel.$isAnyPaymentPopupOpened,
})

/******* Cart requests *******/

sample({
  clock: cartModel.postItemToCartFx.done,
  source: {
    paymentChannel: genericPaymentModel.$prioritizedPaymentChannel,
  },
  filter: ({ paymentChannel }) =>
    paymentChannel?.paymentChannel === PurchasePaymentChannelEnum.GR4VY_GATEWAY,
  fn: () =>
    ({
      payment: {
        cartPaymentChannel: PurchasePaymentChannelEnum.GR4VY_GATEWAY as string,
      },
    }) as ICartProperties,
  target: api.cart.setPaymentDataCartFx,
})
/******* Cart requests *******/

/***** Steps Management ******/

export const $currentStepGravy = createStore<
  (typeof PaymentStepsGravy)[keyof typeof PaymentStepsGravy]
>(PaymentStepsGravy.SelectPriceSettings).reset([
  genericPaymentModel.resetStep,
  closeGravyPopup,
])

sample({
  clock: api.cart.confirmCartPurchaseFx.done,
  source: {
    purchaseProcessIsFailed: genericPaymentModel.$purchaseProcessIsFailed,
    paymentChannel: genericPaymentModel.$prioritizedPaymentChannel,
  },
  filter: ({ purchaseProcessIsFailed, paymentChannel }) =>
    !purchaseProcessIsFailed &&
    paymentChannel?.paymentChannel === PurchasePaymentChannelEnum.GR4VY_GATEWAY,
  fn: () => PaymentStepsGravy.GravyForm,
  target: $currentStepGravy,
})

sample({
  clock: api.purchases.initializePurchaseProcessFx.done,
  source: { paymentChannel: genericPaymentModel.$prioritizedPaymentChannel },
  filter: ({ paymentChannel }) =>
    paymentChannel?.paymentChannel === PurchasePaymentChannelEnum.GR4VY_GATEWAY,
  fn: () => PaymentStepsGravy.SuccessPurchaseConfirmation,
  target: $currentStepGravy,
})

sample({
  clock: genericPaymentModel.goBackToPreviousStep,
  source: $isPending,
  filter: (isPending) => !isPending,
  fn: () => PaymentStepsGravy.SelectPriceSettings,
  target: $currentStepGravy,
})

sample({
  clock: $currentStepGravy,
  target: genericPaymentModel.resetErrors,
})
/***** Steps Management ******/
